const normalizeNumericInput = (value) => String(value ?? '').replace(/\s+/g, '').replace(',', '.').trim();

export const parseInvoiceDecimal = (value, fallback = 0) => {
  const numeric = Number.parseFloat(normalizeNumericInput(value));
  return Number.isFinite(numeric) ? numeric : fallback;
};

export const roundInvoiceAmount = (value) => Math.round((Number(value) || 0) * 100) / 100;

export const formatInvoiceDecimal = (value) => {
  const numeric = roundInvoiceAmount(value);
  if (Number.isInteger(numeric)) return String(numeric);
  return numeric.toFixed(2).replace(/\.?0+$/, '');
};

export const formatInvoiceMoney = (value) => (
  roundInvoiceAmount(value).toLocaleString('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
);

export const getInvoiceRowsTotal = (rows = []) => rows.reduce((sum, row) => {
  const quantity = parseInvoiceDecimal(row?.quantity, 0);
  const price = parseInvoiceDecimal(row?.price, 0);
  return sum + quantity * price;
}, 0);

export const buildInvoiceItemsCountText = (rows = []) => (
  `Всего наименований ${rows.length}, на сумму ${formatInvoiceMoney(getInvoiceRowsTotal(rows))} руб.`
);

const pluralForm = (value, one, few, many) => {
  const number = Math.abs(Number(value) || 0);
  if (number % 10 === 1 && number % 100 !== 11) return one;
  if ([2, 3, 4].includes(number % 10) && ![12, 13, 14].includes(number % 100)) return few;
  return many;
};

const intToWordsRu = (value, feminine = false) => {
  const unitsMale = ['', 'один', 'два', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять'];
  const unitsFemale = ['', 'одна', 'две', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять'];
  const teens = ['десять', 'одиннадцать', 'двенадцать', 'тринадцать', 'четырнадцать', 'пятнадцать', 'шестнадцать', 'семнадцать', 'восемнадцать', 'девятнадцать'];
  const tens = ['', '', 'двадцать', 'тридцать', 'сорок', 'пятьдесят', 'шестьдесят', 'семьдесят', 'восемьдесят', 'девяносто'];
  const hundreds = ['', 'сто', 'двести', 'триста', 'четыреста', 'пятьсот', 'шестьсот', 'семьсот', 'восемьсот', 'девятьсот'];
  const groups = [
    ['', '', '', false],
    ['тысяча', 'тысячи', 'тысяч', true],
    ['миллион', 'миллиона', 'миллионов', false],
    ['миллиард', 'миллиарда', 'миллиардов', false],
  ];

  if (!value) return 'ноль';

  const parts = [];
  let number = Math.abs(Number(value) || 0);
  let groupIndex = 0;

  while (number > 0 && groupIndex < groups.length) {
    const chunk = number % 1000;
    number = Math.floor(number / 1000);
    if (chunk) {
      const words = [];
      words.push(hundreds[Math.floor(chunk / 100)]);
      const remainder = chunk % 100;
      if (remainder >= 10 && remainder <= 19) {
        words.push(teens[remainder - 10]);
      } else {
        words.push(tens[Math.floor(remainder / 10)]);
        const unitWords = (groups[groupIndex][3] || (groupIndex === 0 && feminine)) ? unitsFemale : unitsMale;
        words.push(unitWords[remainder % 10]);
      }
      const suffix = groupIndex > 0 ? pluralForm(chunk, groups[groupIndex][0], groups[groupIndex][1], groups[groupIndex][2]) : '';
      parts.unshift([words.filter(Boolean).join(' '), suffix].filter(Boolean).join(' '));
    }
    groupIndex += 1;
  }

  return parts.join(' ').trim();
};

export const moneyToWordsRu = (value) => {
  const rounded = roundInvoiceAmount(value);
  const rubles = Math.floor(rounded);
  const kopecks = Math.round((rounded - rubles) * 100);
  return `${intToWordsRu(rubles).replace(/^./, (letter) => letter.toUpperCase())} ${pluralForm(rubles, 'рубль', 'рубля', 'рублей')} ${String(kopecks).padStart(2, '0')} ${pluralForm(kopecks, 'копейка', 'копейки', 'копеек')}`;
};

export const buildInvoiceAmountWords = (rows = [], vatText = 'Без налога (НДС)') => {
  const base = moneyToWordsRu(getInvoiceRowsTotal(rows));
  const normalizedVat = String(vatText || '').replace('(НДС)', '').trim().replace(/\.+$/, '');
  return normalizedVat ? `${base}. ${normalizedVat}.` : `${base}.`;
};
